import { type MessageKey } from './messages/fr';

/**
 * The stable error-code vocabulary the API attaches to every generic
 * failure response (`AllExceptionsFilter`'s `errorCode` field,
 * `apps/api/src/common/errors/http-error-codes.ts`). This is the ONE
 * place a caught error gets turned into a `MessageKey` — every app
 * (admin-dashboard, merchant-dashboard, storefront) calls the same
 * function so "network failure" or "session expired" reads identically,
 * in the same three languages, everywhere it can occur.
 *
 * =====================================================================
 * WHY A CODE, NOT THE API's OWN MESSAGE STRING
 * =====================================================================
 *
 * Before this, every call site did `error instanceof ApiError ?
 * error.message : t('merchant.common.unexpectedError')` — showing the
 * API's raw, untranslated, English `message` field whenever the request
 * actually reached a handler (only a true network failure or a missing
 * `ApiError` shape fell back to the translated default). That leaked
 * "Internal Server Error", "Request failed with status 500", stack-free
 * but still technical English sentences straight into the UI, in every
 * language.
 *
 * A stable `errorCode` fixes the coupling problem an earlier attempt at
 * this hit: matching on `error.message === 'Authentication required.'`
 * (`SESSION_EXPIRED` detection, each app's `src/lib/api-client.ts`) breaks the
 * moment that English string changes for any reason, including a future
 * translation. A code is never translated and never means two different
 * things depending on wording.
 *
 * =====================================================================
 * WHAT THIS DELIBERATELY DOES NOT COVER
 * =====================================================================
 *
 * Specific business messages (coupon rejected, slug taken) stay exactly as they are today — real, meaningful, but
 * server-relayed and untranslated (see `messages/fr.ts`'s own header for
 * why that is a separate, still-open problem). A screen that wants its
 * OWN specific wording for a KNOWN condition (e.g. a login form showing
 * "wrong password" inline) should keep doing that; this resolver is the
 * FALLBACK for everything else — the generic surface named explicitly in
 * this sprint (network, server, session, permission, not-found,
 * validation, conflict, rate-limit) — not a replacement for
 * context-specific handling that already exists.
 */
export const ERROR_CODES = [
  'SESSION_EXPIRED',
  'INVALID_CREDENTIALS',
  'FORBIDDEN',
  'NOT_FOUND',
  'VALIDATION_ERROR',
  'CONFLICT',
  'UNSUPPORTED_MEDIA_TYPE',
  'RATE_LIMITED',
  'SERVER_ERROR',
  'SERVICE_UNAVAILABLE',
  'BAD_REQUEST',
  // Credential lifecycle: refusals that carry their own code because the HTTP status alone cannot say which refusal it is.
  'PASSWORD_CHANGE_REQUIRED',
  'CURRENT_PASSWORD_INVALID',
  'PASSWORD_UNCHANGED',
  'PASSWORD_TOO_SHORT',
  'STAFF_ACCOUNT_UNAVAILABLE',
  'STAFF_LIMIT_REACHED',
  'SHOP_NOT_ACTIVE',
  // Re-authentication before a critical action, and the refusals of the operator-side account operations.
  'REAUTH_REQUIRED',
  'REAUTH_FAILED',
  'EMAIL_UNAVAILABLE',
  'ACCOUNT_BELONGS_TO_A_SHOP',
  'OWNER_AMBIGUOUS',
  'OWNER_DISABLED',
  // Platform accounts and the two-person promotion of a superadmin.
  'PLATFORM_ACCOUNT_SELF',
  'LAST_SUPERADMIN',
  'ACCOUNT_STATUS_UNCHANGED',
  'PROMOTION_SELF',
  'PROMOTION_TARGET_NOT_ELIGIBLE',
  'SUPERADMIN_CAP_REACHED',
  'NO_SECOND_SUPERADMIN',
  'PROMOTION_ALREADY_PENDING',
  'PROMOTION_NOT_PENDING',
  'PROMOTION_EXPIRED',
  'PROMOTION_SAME_PERSON',
  // Access and passwords.
  'SUPERADMIN_NOT_RESETTABLE',
  'ACCOUNT_DISABLED',
  'ACCOUNT_NOT_LOCKED',
  // Merchant portfolios (who manages which shop).
  'ACCOUNT_HAS_MERCHANTS',
  'MANAGER_REQUIRED',
  'MANAGER_FORBIDDEN',
  'MANAGER_INVALID',
  'PORTFOLIO_TARGET_INVALID',
  'PORTFOLIO_UNCHANGED',
  'PORTFOLIO_SAME_ADMIN',
  'PORTFOLIO_EMPTY',
  'SUSPENDED_BY_SUPERADMIN',
  'OWNER_IN_USE',
  // Inventory.
  'INSUFFICIENT_STOCK',
  // Object storage (images) unreachable.
  'STORAGE_UNAVAILABLE',
  // Shopper side: cart, checkout, coupon, order lookup.
  'CART_NOT_FOUND',
  'CART_ITEM_NOT_FOUND',
  'CART_EMPTY',
  'PRODUCT_NOT_AVAILABLE',
  'STOCK_EXCEEDED',
  'VARIANT_REQUIRED',
  'VARIANT_NOT_FOUND',
  'COUPON_NOT_FOUND',
  // Manual orders and sales (migration 0096).
  'SALE_DATE_OUT_OF_RANGE',
  'STORE_NOT_CONFIGURED',
  'REQUEST_IN_PROGRESS',
  'COUPON_NOT_YET_VALID',
  'COUPON_EXPIRED',
  'COUPON_MINIMUM_NOT_MET',
  'COUPON_USAGE_LIMIT_REACHED',
  'ORDER_NOT_FOUND',
  // Merchant assistant.
  'ASSISTANT_UNAVAILABLE',
  'ASSISTANT_BUSY',
  'ASSISTANT_ERROR',
  'ASSISTANT_CONVERSATION_NOT_FOUND',
  'ASSISTANT_CONVERSATION_FULL',
  'ASSISTANT_NOTHING_PENDING',
  // Expéditions (Jibex): refusals of a shipment action, each with its own message.
  'SHIPMENT_NOT_FOUND',
  'SHIPMENT_NOT_RETRYABLE',
  'SHIPMENT_NOT_AT_CARRIER',
  'SHIPMENT_NOT_CANCELLABLE',
  'SHIPMENT_NOT_RESENDABLE',
  'SHIPMENT_ALREADY_AT_CARRIER',
  'INVALID_CITY',
  'CARRIER_ACCOUNT_UNAVAILABLE',
  // « Confirmer et envoyer à Jibex ».
  'SHIPMENT_IN_PROGRESS',
  'ORDER_NOT_SENDABLE',
  'INVALID_PHONE',
  'INVALID_PHONE_2',
  'INVALID_NAME',
  'INVALID_ADDRESS',
  'CARRIER_CREDENTIALS_REFUSED',
  'CARRIER_SCOPE_MISSING',
  'CARRIER_RATE_LIMITED',
  'CARRIER_UNAVAILABLE',
  'CARRIER_REFUSED',
  'DELIVERY_LIMITER_UNAVAILABLE',
  'NOTIFICATIONS_UNAVAILABLE',
] as const;

export type ErrorCode = (typeof ERROR_CODES)[number];

/**
 * Structural shape every app's own `ApiError`/`PublicApiError` class is
 * expected to satisfy — duck-typed on purpose so this package never
 * imports any app's error class (that would be a dependency pointing the
 * wrong way). `status` is what already exists everywhere; `errorCode` is
 * the new field each api-client now reads off the JSON error body.
 */
interface HttpLikeError {
  readonly status: number;
  readonly errorCode?: string;
}

function isHttpLikeError(error: unknown): error is HttpLikeError {
  return (
    typeof error === 'object' &&
    error !== null &&
    'status' in error &&
    typeof error.status === 'number'
  );
}

const MESSAGE_KEY_BY_ERROR_CODE: Record<ErrorCode, MessageKey> = {
  SESSION_EXPIRED: 'errors.sessionExpired',
  INVALID_CREDENTIALS: 'errors.forbidden',
  FORBIDDEN: 'errors.forbidden',
  NOT_FOUND: 'errors.notFound',
  VALIDATION_ERROR: 'errors.validation',
  CONFLICT: 'errors.conflict',
  UNSUPPORTED_MEDIA_TYPE: 'errors.validation',
  RATE_LIMITED: 'errors.rateLimited',
  SERVER_ERROR: 'errors.server',
  SERVICE_UNAVAILABLE: 'errors.server',
  BAD_REQUEST: 'errors.server',
  PASSWORD_CHANGE_REQUIRED: 'errors.passwordChangeRequired',
  CURRENT_PASSWORD_INVALID: 'errors.currentPasswordInvalid',
  PASSWORD_UNCHANGED: 'errors.passwordUnchanged',
  PASSWORD_TOO_SHORT: 'errors.passwordTooShort',
  STAFF_ACCOUNT_UNAVAILABLE: 'errors.staffAccountUnavailable',
  STAFF_LIMIT_REACHED: 'errors.staffLimitReached',
  SHOP_NOT_ACTIVE: 'errors.shopNotActive',
  REAUTH_REQUIRED: 'errors.reauthRequired',
  REAUTH_FAILED: 'errors.reauthFailed',
  EMAIL_UNAVAILABLE: 'errors.emailUnavailable',
  ACCOUNT_BELONGS_TO_A_SHOP: 'errors.accountBelongsToShop',
  OWNER_AMBIGUOUS: 'errors.ownerAmbiguous',
  OWNER_DISABLED: 'errors.ownerDisabled',
  PLATFORM_ACCOUNT_SELF: 'errors.platformAccountSelf',
  LAST_SUPERADMIN: 'errors.lastSuperadmin',
  ACCOUNT_STATUS_UNCHANGED: 'errors.accountStatusUnchanged',
  PROMOTION_SELF: 'errors.promotionSelf',
  PROMOTION_TARGET_NOT_ELIGIBLE: 'errors.promotionTargetNotEligible',
  SUPERADMIN_CAP_REACHED: 'errors.superadminCapReached',
  NO_SECOND_SUPERADMIN: 'errors.noSecondSuperadmin',
  PROMOTION_ALREADY_PENDING: 'errors.promotionAlreadyPending',
  PROMOTION_NOT_PENDING: 'errors.promotionNotPending',
  PROMOTION_EXPIRED: 'errors.promotionExpired',
  PROMOTION_SAME_PERSON: 'errors.promotionSamePerson',
  ACCOUNT_HAS_MERCHANTS: 'errors.accountHasMerchants',
  MANAGER_REQUIRED: 'errors.managerRequired',
  MANAGER_FORBIDDEN: 'errors.managerForbidden',
  MANAGER_INVALID: 'errors.managerInvalid',
  PORTFOLIO_TARGET_INVALID: 'errors.portfolioTargetInvalid',
  PORTFOLIO_UNCHANGED: 'errors.portfolioUnchanged',
  PORTFOLIO_SAME_ADMIN: 'errors.portfolioSameAdmin',
  PORTFOLIO_EMPTY: 'errors.portfolioEmpty',
  SUSPENDED_BY_SUPERADMIN: 'errors.suspendedBySuperadmin',
  OWNER_IN_USE: 'errors.ownerInUse',
  SUPERADMIN_NOT_RESETTABLE: 'errors.superadminNotResettable',
  ACCOUNT_DISABLED: 'errors.accountDisabled',
  ACCOUNT_NOT_LOCKED: 'errors.accountNotLocked',
  INSUFFICIENT_STOCK: 'errors.insufficientStock',
  STORAGE_UNAVAILABLE: 'errors.storageUnavailable',
  CART_NOT_FOUND: 'errors.cartNotFound',
  CART_ITEM_NOT_FOUND: 'errors.cartItemNotFound',
  CART_EMPTY: 'errors.cartEmpty',
  PRODUCT_NOT_AVAILABLE: 'errors.productNotAvailable',
  STOCK_EXCEEDED: 'errors.stockExceeded',
  VARIANT_REQUIRED: 'errors.variantRequired',
  VARIANT_NOT_FOUND: 'errors.variantNotFound',
  COUPON_NOT_FOUND: 'errors.couponNotFound',
  SALE_DATE_OUT_OF_RANGE: 'errors.saleDateOutOfRange',
  STORE_NOT_CONFIGURED: 'errors.storeNotConfigured',
  REQUEST_IN_PROGRESS: 'errors.requestInProgress',
  COUPON_NOT_YET_VALID: 'errors.couponNotYetValid',
  COUPON_EXPIRED: 'errors.couponExpired',
  COUPON_MINIMUM_NOT_MET: 'errors.couponMinimumNotMet',
  COUPON_USAGE_LIMIT_REACHED: 'errors.couponUsageLimitReached',
  ORDER_NOT_FOUND: 'errors.orderNotFound',
  ASSISTANT_UNAVAILABLE: 'merchant.assistant.unavailable',
  ASSISTANT_BUSY: 'errors.rateLimited',
  ASSISTANT_ERROR: 'errors.server',
  ASSISTANT_CONVERSATION_NOT_FOUND: 'merchant.assistant.notice.conversationExpired',
  ASSISTANT_CONVERSATION_FULL: 'merchant.assistant.notice.conversationFull',
  ASSISTANT_NOTHING_PENDING: 'merchant.assistant.notice.nothingPending',
  SHIPMENT_NOT_FOUND: 'merchant.ship.error.notFound',
  SHIPMENT_NOT_RETRYABLE: 'merchant.ship.error.notRetryable',
  SHIPMENT_NOT_AT_CARRIER: 'merchant.ship.error.notAtCarrier',
  SHIPMENT_NOT_CANCELLABLE: 'merchant.ship.error.notCancellable',
  SHIPMENT_NOT_RESENDABLE: 'merchant.ship.error.notResendable',
  SHIPMENT_ALREADY_AT_CARRIER: 'merchant.ship.error.alreadyAtCarrier',
  INVALID_CITY: 'merchant.ship.error.invalidCity',
  SHIPMENT_IN_PROGRESS: 'merchant.sendJibex.inProgress',
  ORDER_NOT_SENDABLE: 'merchant.sendJibex.notSendable',
  INVALID_PHONE: 'merchant.ship.lastError.INVALID_PHONE',
  INVALID_PHONE_2: 'merchant.sendJibex.phone2Invalid',
  INVALID_NAME: 'merchant.ship.lastError.INVALID_NAME',
  INVALID_ADDRESS: 'merchant.ship.lastError.INVALID_ADDRESS',
  CARRIER_ACCOUNT_UNAVAILABLE: 'merchant.ship.error.accountUnavailable',
  CARRIER_CREDENTIALS_REFUSED: 'merchant.ship.error.credentialsRefused',
  CARRIER_SCOPE_MISSING: 'merchant.ship.error.scopeMissing',
  CARRIER_RATE_LIMITED: 'merchant.ship.error.carrierRateLimited',
  CARRIER_UNAVAILABLE: 'merchant.ship.error.carrierUnavailable',
  CARRIER_REFUSED: 'merchant.ship.error.carrierRefused',
  DELIVERY_LIMITER_UNAVAILABLE: 'errors.server',
  NOTIFICATIONS_UNAVAILABLE: 'merchant.delivery.notifications.errorUnavailable',
};

/**
 * Maps ANY caught error — a real HTTP error response, a network failure
 * that never reached the server, or something that isn't even an
 * `Error` — to the `MessageKey` a toast/banner/inline error should show.
 *
 * Never returns raw text: the caller still has to call `t()`/`translate()`
 * on the result, so there is no code path back to displaying `.message`.
 */
export function resolveErrorMessageKey(error: unknown): MessageKey {
  if (isHttpLikeError(error)) {
    const code = error.errorCode;
    if (code !== undefined && isKnownErrorCode(code)) {
      return MESSAGE_KEY_BY_ERROR_CODE[code];
    }
    // A real HTTP response came back, but either the server is old
    // (no errorCode yet) or sent one this catalogue doesn't know about.
    // Status-code fallback keeps this safe rather than leaking `.message`.
    return error.status >= 500 ? 'errors.server' : 'errors.forbidden';
  }

  // No `.status` at all: the request never got an HTTP response —
  // offline, DNS failure, CORS, a timeout. This is what a bare
  // `TypeError` from a failed `fetch()` looks like in every one of this
  // project's three api-clients, none of which wrap that case today.
  return 'errors.network';
}

function isKnownErrorCode(value: string): value is ErrorCode {
  return (ERROR_CODES as readonly string[]).includes(value);
}
